import { Plus, Loader2, Check, Dumbbell, X } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import {
  getSportsByCategory,
  getDefaultSportCategories,
} from '../../../lib/defaults/defaultSportsAndAmenities';

interface CompanySport {
  id: string;
  sport_name: string;
  category_id: string | null;
  company_sport_categories?: {
    name: string;
  } | null;
}

interface SportCategory {
  id: string;
  name: string;
  company_id: string | null;
}

interface SportsStepProps {
  companyId: string;
  locationId: string;
  selectedSportIds: string[];
  onSelectionChange: (sportIds: string[]) => void;
}

export default function SportsStep({
  companyId,
  locationId,
  selectedSportIds,
  onSelectionChange,
}: SportsStepProps) {
  const [sports, setSports] = useState<CompanySport[]>([]);
  const [categories, setCategories] = useState<SportCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add sport modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'create' | 'template' | null>(null); // 'create' or 'template'
  const [newSportName, setNewSportName] = useState('');
  const [newSportCategory, setNewSportCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Template selection state
  const [selectedTemplateSports, setSelectedTemplateSports] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch company sports with categories
      const { data: sportsData, error: sportsError } = await supabase
        .from('company_sports')
        .select('*, company_sport_categories(name)')
        .eq('company_id', companyId);

      if (sportsError) throw sportsError;
      setSports(sportsData || []);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('company_sport_categories')
        .select('*')
        .eq('company_id', companyId);

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // If location exists, fetch existing selections
      if (locationId) {
        const { data: existingSports, error: existingError } = await supabase
          .rpc('get_location_sports', { location_id_input: locationId });

        if (!existingError && existingSports) {
          const sportIds = existingSports.map((s: { company_sport_id: string }) => s.company_sport_id);
          onSelectionChange(sportIds);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sports');
    } finally {
      setLoading(false);
    }
  }, [companyId, locationId, onSelectionChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSport = (sportId: string) => {
    if (selectedSportIds.includes(sportId)) {
      onSelectionChange(selectedSportIds.filter(id => id !== sportId));
    } else {
      onSelectionChange([...selectedSportIds, sportId]);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setAddingItem(true);

    try {
      const { data, error } = await supabase
        .from('company_sport_categories')
        .insert({ name: newCategoryName.trim(), company_id: companyId })
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setNewSportCategory(data.id);
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add category');
    } finally {
      setAddingItem(false);
    }
  };

  const handleAddSport = async () => {
    if (!newSportName.trim() || !newSportCategory) return;
    setAddingItem(true);

    try {
      const { data, error } = await supabase
        .from('company_sports')
        .insert({
          sport_name: newSportName.trim(),
          category_id: newSportCategory,
          company_id: companyId,
        })
        .select('*, company_sport_categories(name)')
        .single();

      if (error) throw error;

      setSports([...sports, data]);
      // Auto-select the new sport
      onSelectionChange([...selectedSportIds, data.id]);

      // Reset form
      setNewSportName('');
      setNewSportCategory('');
      setShowAddModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add sport');
    } finally {
      setAddingItem(false);
    }
  };

  // Group sports by category
  const groupedSports = sports.reduce<Record<string, CompanySport[]>>((acc, sport) => {
    const categoryName = sport.company_sport_categories?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(sport);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-rollops-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-3">
          <Dumbbell size={24} className="text-rollops-accent" />
        </div>
        <h3 className="text-lg font-semibold text-[#EBFFEE]">Sports & Activities</h3>
        <p className="text-sm text-[#A9A3A3] mt-1">
          Select the sports and activities offered at this location
        </p>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Sports Selection */}
      {sports.length === 0 ? (
        <div className="text-center py-8 bg-[#221F1F] rounded-xl border border-[#504A4A]">
          <p className="text-[#A9A3A3] mb-4">No sports have been added yet.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
          >
            <Plus size={18} />
            Add First Sport
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedSports).map(([categoryName, categorySports]) => (
            <div key={categoryName} className="bg-[#221F1F] rounded-xl p-4 border border-[#504A4A]">
              <h4 className="text-sm font-medium text-[#A9A3A3] mb-3 uppercase tracking-wider">
                {categoryName}
              </h4>
              <div className="flex flex-wrap gap-2">
                {categorySports.map(sport => {
                  const isSelected = selectedSportIds.includes(sport.id);
                  return (
                    <button
                      key={sport.id}
                      onClick={() => toggleSport(sport.id)}
                      className={`
                        px-4 py-2 rounded-full text-sm font-medium transition-all
                        ${isSelected
                          ? 'bg-[#9B1421] text-white'
                          : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454] hover:text-[#EBFFEE]'
                        }
                      `}
                    >
                      {isSelected && <Check size={14} className="inline mr-1" />}
                      {sport.sport_name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Add Sport Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full py-3 border-2 border-dashed border-[#504A4A] rounded-xl text-[#A9A3A3] hover:border-[#9B1421] hover:text-rollops-accent transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Add Custom Sport
          </button>
        </div>
      )}

      {/* Selected Count */}
      <div className="text-center text-sm text-[#A9A3A3]">
        {selectedSportIds.length} sport{selectedSportIds.length !== 1 ? 's' : ''} selected
      </div>

      {/* Add Sport Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-[#221F1F] rounded-xl p-6 border border-[#504A4A] ${
            addMode === 'template' ? 'w-full max-w-2xl max-h-[90vh] overflow-y-auto' : 'w-full max-w-md'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#EBFFEE]">
                {addMode === null ? 'Add Sport' : addMode === 'create' ? 'Create New Sport' : 'Pick from Template'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode(null);
                  setNewSportName('');
                  setNewSportCategory('');
                  setShowNewCategory(false);
                  setNewCategoryName('');
                  setSelectedTemplateSports(new Set());
                }}
                className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Mode Selection - Show when no mode selected */}
            {addMode === null && (
              <div className="space-y-4">
                <p className="text-sm text-[#A9A3A3] mb-4">
                  Choose how you want to add a sport:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => setAddMode('create')}
                    className="p-4 bg-[#504A4A] hover:bg-[#5A5454] rounded-lg text-left transition-colors border border-transparent hover:border-[#9B1421]/50"
                  >
                    <div className="font-semibold text-[#EBFFEE] mb-1">Create New</div>
                    <div className="text-sm text-[#A9A3A3]">Create a custom sport with your own name and category</div>
                  </button>
                  <button
                    onClick={() => setAddMode('template')}
                    className="p-4 bg-[#504A4A] hover:bg-[#5A5454] rounded-lg text-left transition-colors border border-transparent hover:border-[#9B1421]/50"
                  >
                    <div className="font-semibold text-[#EBFFEE] mb-1">Pick from Template</div>
                    <div className="text-sm text-[#A9A3A3]">Select from predefined sports. You can pick individual items - selecting one doesn&apos;t add all.</div>
                  </button>
                </div>
              </div>
            )}

            {/* Create New Mode */}
            {addMode === 'create' && (
              <div className="space-y-4">
                {/* Category Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#EBFFEE]">Category</label>
                {!showNewCategory ? (
                  <div className="space-y-2">
                    <select
                      value={newSportCategory}
                      onChange={(e) => {
                        if (e.target.value === 'new') {
                          setShowNewCategory(true);
                        } else {
                          setNewSportCategory(e.target.value);
                        }
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                      <option value="new">+ Create New Category</option>
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowNewCategory(false);
                          setNewCategoryName('');
                        }}
                        className="flex-1 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim() || addingItem}
                        className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] text-white rounded-lg"
                      >
                        {addingItem ? 'Creating...' : 'Create Category'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

                {/* Sport Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#EBFFEE]">Sport Name</label>
                  <input
                    type="text"
                    value={newSportName}
                    onChange={(e) => setNewSportName(e.target.value)}
                    placeholder="e.g., Brazilian Jiu-Jitsu"
                    className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                  />
                </div>

              </div>
            )}

            {/* Template Mode */}
            {addMode === 'template' && (
              <div className="space-y-4">
                <p className="text-sm text-[#A9A3A3] mb-4">
                  Select sports from the template. You can pick individual items - selecting one doesn&apos;t add all.
                </p>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {getDefaultSportCategories().map(category => {
                    const categorySports = getSportsByCategory(category);
                    const existingSportNames = new Set(sports.map(s => s.sport_name.toLowerCase()));
                    
                    return (
                      <div key={category} className="bg-[#363232] rounded-lg p-4 border border-[#504A4A]">
                        <h4 className="text-sm font-semibold text-[#EBFFEE] mb-3 uppercase tracking-wider">
                          {category}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categorySports.map(sport => {
                            const isSelected = selectedTemplateSports.has(`${category}::${sport.name}`);
                            const alreadyExists = existingSportNames.has(sport.name.toLowerCase());
                            const key = `${category}::${sport.name}`;
                            
                            return (
                              <button
                                key={key}
                                onClick={() => {
                                  if (alreadyExists) return;
                                  const newSet = new Set(selectedTemplateSports);
                                  if (isSelected) {
                                    newSet.delete(key);
                                  } else {
                                    newSet.add(key);
                                  }
                                  setSelectedTemplateSports(newSet);
                                }}
                                disabled={alreadyExists}
                                className={`
                                  px-3 py-1.5 text-sm rounded-full transition-all flex items-center gap-1
                                  ${alreadyExists
                                    ? 'bg-[#504A4A]/50 text-[#A9A3A3]/50 cursor-not-allowed'
                                    : isSelected
                                      ? 'bg-[#9B1421] text-white'
                                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454] hover:text-[#EBFFEE]'
                                  }
                                `}
                                title={alreadyExists ? 'This sport already exists' : ''}
                              >
                                {isSelected && <Check size={14} />}
                                {sport.name}
                                {alreadyExists && <span className="text-xs ml-1">(exists)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedTemplateSports.size > 0 && (
                  <div className="bg-[#9B1421]/20 border border-[#9B1421]/50 rounded-lg p-3">
                    <p className="text-sm text-[#EBFFEE]">
                      {selectedTemplateSports.size} sport{selectedTemplateSports.size !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              {addMode !== null && (
                <button
                  onClick={() => {
                    setAddMode(null);
                    setNewSportName('');
                    setNewSportCategory('');
                    setShowNewCategory(false);
                    setNewCategoryName('');
                    setSelectedTemplateSports(new Set());
                  }}
                  className="px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode(null);
                  setNewSportName('');
                  setNewSportCategory('');
                  setShowNewCategory(false);
                  setNewCategoryName('');
                  setSelectedTemplateSports(new Set());
                }}
                className={`${addMode === null ? 'flex-1' : ''} py-2 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg`}
              >
                Cancel
              </button>
              {addMode === 'create' && (
                <button
                  onClick={handleAddSport}
                  disabled={!newSportName.trim() || !newSportCategory || addingItem}
                  className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] text-white rounded-lg"
                >
                  {addingItem ? 'Adding...' : 'Add Sport'}
                </button>
              )}
              {addMode === 'template' && (
                <button
                  onClick={async () => {
                    if (selectedTemplateSports.size === 0) return;
                    
                    setAddingItem(true);
                    try {
                      // Get or create categories first
                      const categoryMap = new Map<string, string>();
                      
                      for (const key of selectedTemplateSports) {
                        const [category] = key.split('::');
                        if (!categoryMap.has(category)) {
                          // Check if category exists
                          let categoryId = categories.find(c => c.name === category)?.id;
                          
                          if (!categoryId) {
                            // Create category
                            const { data: newCat, error: catError } = await supabase
                              .from('company_sport_categories')
                              .insert({ name: category, company_id: companyId })
                              .select()
                              .single();
                            
                            if (catError) throw catError;
                            categoryId = newCat.id;
                            setCategories(prev => [...prev, newCat]);
                          }
                          
                          categoryMap.set(category, categoryId);
                        }
                      }
                      
                      // Add selected sports
                      const sportsToAdd = Array.from(selectedTemplateSports).map(key => {
                        const [category, name] = key.split('::');
                        const categoryId = categoryMap.get(category);
                        if (!categoryId) {
                          throw new Error(`Category not found: ${category}`);
                        }
                        return {
                          sport_name: name,
                          category_id: categoryId,
                          company_id: companyId,
                        };
                      });
                      
                      const { data: newSports, error: sportsError } = await supabase
                        .from('company_sports')
                        .insert(sportsToAdd)
                        .select('*, company_sport_categories(name)');
                      
                      if (sportsError) throw sportsError;
                      
                      setSports(prev => [...prev, ...newSports]);
                      
                      // Auto-select the newly added sports for the current location
                      const newSportIds = newSports.map(sport => sport.id);
                      const updatedSelections = [...selectedSportIds, ...newSportIds];
                      onSelectionChange(updatedSelections);
                      
                      setShowAddModal(false);
                      setAddMode(null);
                      setSelectedTemplateSports(new Set());
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to add sports');
                    } finally {
                      setAddingItem(false);
                    }
                  }}
                  disabled={selectedTemplateSports.size === 0 || addingItem}
                  className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white rounded-lg"
                >
                  {addingItem ? 'Adding...' : `Add ${selectedTemplateSports.size} Selected`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

